<template>
    <div ref="stage" class="relative w-full overflow-hidden bg-black" style="aspect-ratio: 9 / 16">
        <iframe
            v-if="data.url"
            :key="reloadKey"
            :src="data.url"
            class="absolute top-0 left-0 origin-top-left pointer-events-none"
            style="width: 1080px; height: 1920px; border: 0"
            :style="{ transform: `scale(${scale})` }"
            tabindex="-1"
        ></iframe>

        <div class="absolute z-10 flex gap-2 top-2 right-2">
            <Button
                v-if="data.can_refresh"
                color="primary"
                size="sm"
                :title="lang('tv::tv.widgets.screen.refresh')"
                :loading="refreshing"
                :disabled="refreshing"
                @click="refresh"
            >
                <Icon icon="phosphor/regular/arrows-clockwise" class="size-5" />
            </Button>

            <Button
                v-if="data.url"
                :href="data.url"
                a
                target="_blank"
                size="sm"
                :title="lang('tv::tv.widgets.screen.open')"
            >
                <Icon icon="phosphor/regular/arrow-square-out" class="size-5" />
            </Button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { lang } from '@/lang/composable';
import { useToast } from '@src/Common/Composables/useToast';

interface Props {
    settings: Record<string, unknown>;
    slug: string;
}

interface TvScreenData {
    url: string | null;
    refresh_route: string | null;
    can_refresh: boolean;
}

const props = defineProps<Props>();

const data = ref<TvScreenData>({
    url: null,
    refresh_route: null,
    can_refresh: false,
});

const stage = ref<HTMLElement | null>(null);
const scale = ref(0.25);
const reloadKey = ref(0);
const refreshing = ref(false);

let resizeObserver: ResizeObserver | null = null;

const rescale = () => {
    if (stage.value) {
        scale.value = stage.value.clientWidth / 1080;
    }
};

const load = () => {
    axios.get(route('admin.dashboards.widgets.data', { slug: props.slug })).then(({ data: response }) => {
        data.value = response;
    });
};

const refresh = () => {
    if (!data.value.refresh_route) {
        return;
    }

    refreshing.value = true;

    axios
        .post(data.value.refresh_route)
        .then(() => {
            useToast({ message: lang('tv::tv.widgets.screen.refreshed') });

            // Give the TV screens a moment to reload, then refresh the preview too.
            setTimeout(() => {
                reloadKey.value += 1;
            }, 1500);
        })
        .finally(() => {
            refreshing.value = false;
        });
};

onMounted(() => {
    load();
    rescale();

    resizeObserver = new ResizeObserver(rescale);

    if (stage.value) {
        resizeObserver.observe(stage.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});
</script>
